import React, { useState, useEffect, useRef } from 'react';
import config from '../config';
import { API_ENDPOINTS as ROUTING_ENDPOINTS } from '../config/apiRouting';
import MenuItemImageUpload from './MenuItemImageUpload';
import S3Service from '../utils/s3Service';
import ScrollableModal from './ScrollableModal';
import './MenuItemForm.css';

interface MenuItemFormProps {
  onSubmit: (formData: any) => void;
  onCancel: () => void;
  initialData?: any;
  enterprises: any[];
}

const MenuItemForm: React.FC<MenuItemFormProps> = ({ 
  onSubmit, 
  onCancel, 
  initialData, 
  enterprises 
}) => {
  const [formData, setFormData] = useState(() => {
    const baseData = {
      name: '',
      description: '',
      category: '',
      subcategory: '',
      price: '',
      cost: '',
      enterpriseId: '',
      conceptId: '',
      franchiseeId: '',
      storeId: '',
      allergenInfo: '',
      nutritionalInfo: '',
      preparationTime: '',
      difficulty: 'easy',
      isActive: true,
      isAvailable: true,
      canBeModifier: false,
      sortOrder: '',
      tags: '',
      imageUrl: '',
      // New image fields
      menuImage: '',
      onlineImage: '',
      qualityControlImages: ['', '', '', ''],
      // Digital signage video field
      productVideo: ''
    };

    if (initialData) {
      console.log('MenuItemForm: Initial data received:', initialData);
      console.log('MenuItemForm: Initial isActive:', initialData.isActive, 'isAvailable:', initialData.isAvailable);
      console.log('MenuItemForm: Initial active:', initialData.active, 'available:', initialData.available);
      const formData = {
        ...baseData,
        ...initialData,
        // Explicitly handle boolean fields that might be undefined, checking both field name formats
        isActive: initialData.isActive !== undefined ? initialData.isActive : (initialData.active !== undefined ? initialData.active : true),
        isAvailable: initialData.isAvailable !== undefined ? initialData.isAvailable : (initialData.available !== undefined ? initialData.available : true),
        price: initialData.basePrice || initialData.price || '',
        allergenInfo: Array.isArray(initialData.allergenInfo) 
          ? initialData.allergenInfo.join(', ') 
          : initialData.allergenInfo || '',
        tags: Array.isArray(initialData.tags) 
          ? initialData.tags.join(', ') 
          : initialData.tags || '',
        menuImage: initialData.menuImage || initialData.imageUrl || '',
        onlineImage: initialData.onlineImage || initialData.imageUrl || '',
        qualityControlImages: Array.isArray(initialData.qualityControlImages) && initialData.qualityControlImages.length === 4
          ? initialData.qualityControlImages
          : [...(initialData.qualityControlImages || []), '', '', '', ''].slice(0, 4),
        productVideo: initialData.productVideo || ''
      };
      
      console.log('MenuItemForm: Final form data - isActive:', formData.isActive, 'isAvailable:', formData.isAvailable);
      return formData;
    }

    return baseData;
  });

  const [concepts, setConcepts] = useState<any[]>([]);
  const [franchisees, setFranchisees] = useState<any[]>([]);
  const [stores, setStores] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [generatingDescription, setGeneratingDescription] = useState(false);
  const [tempMenuItemId] = useState(() => S3Service.generateTempMenuItemId());
  
  // Video upload state
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  const videoFileInputRef = useRef<HTMLInputElement>(null);

  const difficulties = [
    { value: 'easy', label: 'Easy' },
    { value: 'medium', label: 'Medium' },
    { value: 'hard', label: 'Hard' },
    { value: 'expert', label: 'Expert' }
  ];

  // Load concepts, franchisees, and stores when enterprise changes
  useEffect(() => {
    console.log(' MenuItemForm: Enterprise selection changed:', formData.enterpriseId);
    console.log(' MenuItemForm: Available enterprises:', enterprises);
    if (formData.enterpriseId) {
      loadEnterpriseData(formData.enterpriseId);
    }
  }, [formData.enterpriseId]);

  // No need to manage body scroll - ScrollableModal handles this

  const loadEnterpriseData = async (enterpriseId: string) => {
    try {
      console.log(' Loading enterprise data for:', enterpriseId);
      setLoading(true);
      
      // Load concepts
      console.log(' Loading concepts from:', ROUTING_ENDPOINTS.concepts(enterpriseId));
      const conceptsResponse = await fetch(ROUTING_ENDPOINTS.concepts(enterpriseId));
      if (conceptsResponse.ok) {
        const conceptsData = await conceptsResponse.json();
        console.log(' Concepts loaded:', conceptsData.data?.length || 0);
        setConcepts(conceptsData.data || []);
      } else {
        console.warn(' Failed to load concepts:', conceptsResponse.status);
      }

      // Load franchisees
      console.log(' Loading franchisees from:', ROUTING_ENDPOINTS.franchisees(enterpriseId));
      const franchiseesResponse = await fetch(ROUTING_ENDPOINTS.franchisees(enterpriseId));
      if (franchiseesResponse.ok) {
        const franchiseesData = await franchiseesResponse.json();
        console.log(' Franchisees loaded:', franchiseesData.data?.length || 0);
        setFranchisees(franchiseesData.data || []);
      } else {
        console.warn(' Failed to load franchisees:', franchiseesResponse.status);
      }

      // Load stores
      console.log(' Loading stores from:', ROUTING_ENDPOINTS.enterpriseStores(enterpriseId));
      const storesResponse = await fetch(ROUTING_ENDPOINTS.enterpriseStores(enterpriseId));
      if (storesResponse.ok) {
        const storesData = await storesResponse.json();
        console.log(' Stores loaded:', storesData.data?.length || 0);
        setStores(storesData.data || []);
      } else {
        console.warn(' Failed to load stores:', storesResponse.status);
      }

      // Load categories from dedicated categories API
      const categoriesUrl = ROUTING_ENDPOINTS.categories(enterpriseId);
      console.log(' Loading categories from:', categoriesUrl);
      const categoriesResponse = await fetch(categoriesUrl);
      if (categoriesResponse.ok) {
        const categoriesData = await categoriesResponse.json();
        console.log(' Categories loaded:', categoriesData.data?.length || 0);
        setCategories(categoriesData.data || []);
      } else {
        console.warn(' Failed to load categories:', categoriesResponse.status);
      }
    } catch (error) {
      console.error(' Error loading enterprise data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    console.log(`MenuItemForm: Setting ${field} to:`, value);
    setFormData((prev: any) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImagesUpdate = (images: {
    menuImage?: string;
    onlineImage?: string;
    qualityControlImages: string[];
  }) => {
    setFormData((prev: any) => ({
      ...prev,
      menuImage: images.menuImage || '',
      onlineImage: images.onlineImage || '',
      qualityControlImages: images.qualityControlImages
    }));
  };

  const handleGenerateAIImage = async () => {
    if (!formData.name.trim()) {
      alert('Please enter a menu item name before generating an image.');
      return;
    }

    setGeneratingImage(true);
    
    try {
      const response = await fetch(ROUTING_ENDPOINTS.generateImage(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description,
          category: formData.category,
          menuItemId: initialData?.menuItemId || initialData?.id || tempMenuItemId,
          enterpriseId: formData.enterpriseId
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to generate image: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        // Update the form with the generated image URL
        setFormData((prev: any) => ({
          ...prev,
          menuImage: result.imageUrl,
          onlineImage: result.imageUrl,
          imageUrl: result.imageUrl
        }));
        
        // Update the images in the MenuItemImageUpload component
        handleImagesUpdate({
          menuImage: result.imageUrl,
          onlineImage: result.imageUrl,
          qualityControlImages: [result.imageUrl, '', '', '']
        });
        
        alert('AI image generated successfully!');
      } else {
        throw new Error(result.message || 'Failed to generate image');
      }
    } catch (error) {
      console.error('Error generating AI image:', error);
      alert(`Failed to generate image: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setGeneratingImage(false);
    }
  };

  const handleGenerateAIDescription = async () => {
    if (!formData.name.trim()) {
      alert('Please enter a menu item name before generating a description.');
      return;
    }

    setGeneratingDescription(true);
    
    try {
      // Check if this is a beer item
      const isBeer = /beer|lager|ale|stout|porter|pilsner|ipa|brewery|brew/i.test(formData.category || '');
      
      const response = await fetch(ROUTING_ENDPOINTS.generateDescription(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          category: formData.category,
          existingDescription: formData.description,
          price: formData.price,
          isBeer: isBeer,
          includeBeerDetails: isBeer // Request ABV, IBU, calories, carbs, tasting notes for beer items
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to generate description: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        // Update the form with the generated description
        setFormData((prev: any) => ({
          ...prev,
          description: result.description
        }));
        
        // If beer details were provided, show them to the user
        if (result.beerDetails && isBeer) {
          const beerInfo = [];
          if (result.beerDetails.abv) beerInfo.push(`ABV: ${result.beerDetails.abv}`);
          if (result.beerDetails.ibu) beerInfo.push(`IBU: ${result.beerDetails.ibu}`);
          if (result.beerDetails.calories) beerInfo.push(`Calories: ${result.beerDetails.calories}`);
          if (result.beerDetails.carbohydrates) beerInfo.push(`Carbs: ${result.beerDetails.carbohydrates}`);
          
          if (beerInfo.length > 0) {
            alert(`AI description generated successfully!\n\nBeer Details Included:\n${beerInfo.join('\n')}`);
          } else {
            alert('AI description generated successfully!');
          }
        } else {
          alert('AI description generated successfully!');
        }
      } else {
        throw new Error(result.message || 'Failed to generate description');
      }
    } catch (error) {
      console.error('Error generating AI description:', error);
      alert(`Failed to generate description: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setGeneratingDescription(false);
    }
  };

  const handleVideoFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    
    const file = e.target.files[0];
    
    try {
      setUploadingVideo(true);
      setVideoUploadProgress(0);
      
      // Simulate progress updates (real progress would come from S3Service)
      const progressInterval = setInterval(() => {
        setVideoUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);
      
      // Upload video using S3Service
      const result = await S3Service.uploadVideo(file, tempMenuItemId);
      
      clearInterval(progressInterval);
      setVideoUploadProgress(100);
      
      if (result.success && result.url) {
        // Update the form data with the uploaded video URL
        handleInputChange('productVideo', result.url);
        
        // Clear progress after a short delay
        setTimeout(() => {
          setVideoUploadProgress(0);
        }, 2000);
      } else {
        throw new Error(result.error || 'Video upload failed');
      }
    } catch (error) {
      console.error('Video upload error:', error);
      alert(`Video upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setUploadingVideo(false);
      // Clear the file input
      if (e.target) {
        e.target.value = '';
      }
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const submitData = {
      ...formData,
      price: parseFloat(formData.price) || 0,
      cost: parseFloat(formData.cost) || 0,
      preparationTime: parseInt(formData.preparationTime) || 0,
      sortOrder: parseInt(formData.sortOrder) || 0,
      allergenInfo: formData.allergenInfo.split(',').map((s: string) => s.trim()).filter(Boolean),
      tags: formData.tags.split(',').map((s: string) => s.trim()).filter(Boolean),
      nutritionalInfo: (() => {
        try {
          return formData.nutritionalInfo && typeof formData.nutritionalInfo === 'string' 
            ? JSON.parse(formData.nutritionalInfo) 
            : (formData.nutritionalInfo || {});
        } catch (e) {
          console.warn('Invalid nutritional info JSON, using empty object:', formData.nutritionalInfo);
          return {};
        }
      })(),
      // Include image data
      menuImage: formData.menuImage,
      onlineImage: formData.onlineImage,
      qualityControlImages: formData.qualityControlImages,
      // Keep the old imageUrl for backward compatibility
      imageUrl: formData.menuImage || formData.imageUrl
    };

    console.log('MenuItemForm: Submitting data with category:', submitData.category);
    console.log('MenuItemForm: Full submit data:', submitData);
    onSubmit(submitData);
  };

  return (
    <ScrollableModal
      isOpen={true}
      onClose={onCancel}
      title={initialData ? 'Edit Menu Item' : 'Create New Menu Item'}
    >
      <form onSubmit={handleSubmit} className="menu-item-form">
            {/* Basic Information */}
            <div className="form-section">
              <h3>Basic Information</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="name">Item Name *</label>
                  <input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="form-input"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="category">Category *</label>
                  <select 
                    id="category"
                    value={formData.category} 
                    onChange={(e) => handleInputChange('category', e.target.value)}
                    className="form-select"
                    required
                  >
                    <option value="">Select category</option>
                    {categories.map(category => (
                      <option key={category.categoryId} value={category.name}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="price">Price ($) *</label>
                  <input
                    id="price"
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => handleInputChange('price', e.target.value)}
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="cost">Cost ($)</label>
                  <input
                    id="cost"
                    type="number"
                    step="0.01"
                    value={formData.cost}
                    onChange={(e) => handleInputChange('cost', e.target.value)}
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <div className="description-field-container">
                  <textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={3}
                    className="form-textarea"
                  />
                  <button
                    type="button"
                    onClick={handleGenerateAIDescription}
                    disabled={generatingDescription || !formData.name.trim()}
                    className="btn-ai-description"
                    title={!formData.name.trim() ? 'Enter a menu item name first' : 'Use AI to generate or improve the description'}
                  >
                    {generatingDescription ? (
                      <>
                        <span className="loading-spinner"></span>
                        Generating...
                      </>
                    ) : (
                      <>
                        <span className="ai-icon">[AI]</span>
                        Use AI to Complete Description
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Location Hierarchy */}
            <div className="form-section">
              <h3>Location</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="enterpriseId">Enterprise *</label>
                  <select 
                    id="enterpriseId"
                    value={formData.enterpriseId} 
                    onChange={(e) => handleInputChange('enterpriseId', e.target.value)}
                    className="form-select"
                    required
                  >
                    <option value="">Select enterprise</option>
                    {enterprises.map(enterprise => (
                      <option key={enterprise.enterpriseId || enterprise.id} value={enterprise.enterpriseId || enterprise.id}>
                        {enterprise.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="conceptId">Concept</label>
                  <select 
                    id="conceptId"
                    value={formData.conceptId} 
                    onChange={(e) => handleInputChange('conceptId', e.target.value)}
                    className="form-select"
                    disabled={!formData.enterpriseId || loading}
                  >
                    <option value="">Select concept</option>
                    {concepts.map(concept => (
                      <option key={concept.conceptId || concept.id} value={concept.conceptId || concept.id}>
                        {concept.name}
                      </option>
                    ))}
                  </select>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    DEBUG: concepts.length={concepts.length}, loading={String(loading)}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="franchiseeId">Franchisee</label>
                  <select 
                    id="franchiseeId"
                    value={formData.franchiseeId} 
                    onChange={(e) => handleInputChange('franchiseeId', e.target.value)}
                    className="form-select"
                    disabled={!formData.enterpriseId || loading}
                  >
                    <option value="">Select franchisee</option>
                    {franchisees.map(franchisee => (
                      <option key={franchisee.franchiseeId || franchisee.id} value={franchisee.franchiseeId || franchisee.id}>
                        {franchisee.name}
                      </option>
                    ))}
                  </select>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    DEBUG: franchisees.length={franchisees.length}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="storeId">Store</label>
                  <select 
                    id="storeId"
                    value={formData.storeId} 
                    onChange={(e) => handleInputChange('storeId', e.target.value)}
                    className="form-select"
                    disabled={!formData.enterpriseId || loading}
                  >
                    <option value="">Select store</option>
                    {stores.map(store => (
                      <option key={store.storeId || store.id} value={store.storeId || store.id}>
                        {store.name}
                      </option>
                    ))}
                  </select>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    DEBUG: stores.length={stores.length}
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Details */}
            <div className="form-section">
              <h3>Additional Details</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="subcategory">Subcategory</label>
                  <input
                    id="subcategory"
                    type="text"
                    value={formData.subcategory}
                    onChange={(e) => handleInputChange('subcategory', e.target.value)}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="preparationTime">Prep Time (minutes)</label>
                  <input
                    id="preparationTime"
                    type="number"
                    value={formData.preparationTime}
                    onChange={(e) => handleInputChange('preparationTime', e.target.value)}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="difficulty">Difficulty</label>
                  <select 
                    id="difficulty"
                    value={formData.difficulty} 
                    onChange={(e) => handleInputChange('difficulty', e.target.value)}
                    className="form-select"
                  >
                    {difficulties.map(diff => (
                      <option key={diff.value} value={diff.value}>
                        {diff.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="imageUrl">Image URL</label>
                  <div className="input-with-button">
                    <input
                      id="imageUrl"
                      type="url"
                      value={formData.imageUrl}
                      onChange={(e) => handleInputChange('imageUrl', e.target.value)}
                      className="form-input"
                      placeholder="https://example.com/image.jpg"
                    />
                    <button
                      type="button"
                      onClick={handleGenerateAIImage}
                      disabled={generatingImage || !formData.name.trim()}
                      className="btn-ai-generate"
                      title="Generate AI image using menu item name and description"
                    >
                      {generatingImage ? (
                        <>
                          <span className="spinner"></span>
                          Generating...
                        </>
                      ) : (
                        <>
                          Use AI to Generate Image
                        </>
                      )}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="productVideo">Product Video (for Digital Signage)</label>
                  <div className="video-upload-section">
                    <input
                      id="productVideo"
                      type="url"
                      value={formData.productVideo}
                      onChange={(e) => handleInputChange('productVideo', e.target.value)}
                      className="form-input"
                      placeholder="https://example.com/video.mp4"
                    />
                    <div className="video-upload-controls">
                      <input
                        type="file"
                        ref={videoFileInputRef}
                        onChange={handleVideoFileSelect}
                        accept="video/mp4,video/webm,video/avi,video/mov,video/quicktime"
                        style={{ display: 'none' }}
                      />
                      <button
                        type="button"
                        onClick={() => videoFileInputRef.current?.click()}
                        className="upload-video-btn"
                        disabled={uploadingVideo}
                      >
                        {uploadingVideo ? 'Uploading...' : 'Upload Video'}
                      </button>
                      {videoUploadProgress > 0 && videoUploadProgress < 100 && (
                        <div className="video-upload-progress">
                          <div className="progress-bar">
                            <div 
                              className="progress-fill" 
                              style={{ width: `${videoUploadProgress}%` }}
                            ></div>
                          </div>
                          <span>{videoUploadProgress}%</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <small className="form-hint">
                    Upload video files directly to S3 or paste an existing URL. Supports MP4, WebM, and other web-compatible formats.
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="allergenInfo">Allergens (comma-separated)</label>
                  <input
                    id="allergenInfo"
                    type="text"
                    value={formData.allergenInfo}
                    onChange={(e) => handleInputChange('allergenInfo', e.target.value)}
                    className="form-input"
                    placeholder="e.g. nuts, dairy, gluten"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="tags">Tags (comma-separated)</label>
                  <input
                    id="tags"
                    type="text"
                    value={formData.tags}
                    onChange={(e) => handleInputChange('tags', e.target.value)}
                    className="form-input"
                    placeholder="e.g. spicy, vegetarian, popular"
                  />
                </div>
              </div>
            </div>

            {/* Images Section */}
            <div className="form-section">
              <MenuItemImageUpload
                menuItemId={initialData?.id || tempMenuItemId}
                images={{
                  menuImage: formData.menuImage,
                  onlineImage: formData.onlineImage,
                  qualityControlImages: formData.qualityControlImages
                }}
                onImagesUpdate={handleImagesUpdate}
              />
            </div>

            {/* Status Options */}
            <div className="form-section">
              <h3>Status</h3>
              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => handleInputChange('isActive', e.target.checked)}
                    className="form-checkbox"
                  />
                  <span>Active</span>
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isAvailable}
                    onChange={(e) => handleInputChange('isAvailable', e.target.checked)}
                    className="form-checkbox"
                  />
                  <span>Available</span>
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.canBeModifier}
                    onChange={(e) => handleInputChange('canBeModifier', e.target.checked)}
                    className="form-checkbox"
                  />
                  <span>Can be Modifier</span>
                </label>
              </div>
            </div>
        </form>

        {/* Form Actions */}
        <div className="modal-footer">
          <button type="button" className="btn-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button 
            type="submit" 
            className="btn-submit"
            disabled={!formData.name || !formData.category || !formData.enterpriseId}
            onClick={handleSubmit}
          >
            {initialData ? 'Update' : 'Create'} Menu Item
          </button>
        </div>
    </ScrollableModal>
  );
};

export default MenuItemForm;